/**
 * Measure and milestone types and computation utilities.
 *
 * All measure/milestone data is stored E2E-encrypted inside `metrics_encrypted`
 * on the Goal. This module provides the client-side types and pure functions
 * needed to work with that decrypted JSON.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface MilestoneAchievement {
  milestone_index: number; // 0-based
  achieved_value: number | null; // null = not yet recorded
  recorded_at: string | null; // ISO datetime
}

export interface Measure {
  id: string; // UUID, client-generated
  name: string; // e.g. "Weight"
  unit: string; // e.g. "lb"
  start_value: number; // e.g. 200
  target_value: number; // e.g. 170
  milestones: MilestoneAchievement[];
}

export interface MilestonePeriod {
  index: number;
  start: Date;
  end: Date;
}

// ── Computation helpers ──────────────────────────────────────────────────────

/**
 * Compute the target value for a specific milestone using linear interpolation.
 * Milestone i (0-based) out of `count` total milestones targets:
 *   start_value + (target_value - start_value) * ((i + 1) / count)
 */
export function computeMilestoneTarget(
  measure: Measure,
  milestoneIndex: number,
  milestoneCount: number
): number {
  const fraction = (milestoneIndex + 1) / milestoneCount;
  return measure.start_value + (measure.target_value - measure.start_value) * fraction;
}

/**
 * Divide the goal's time period into `count` equal milestone periods.
 */
export function computeMilestoneDates(
  startDate: Date,
  endDate: Date,
  count: number
): MilestonePeriod[] {
  const totalMs = endDate.getTime() - startDate.getTime();
  const periodMs = totalMs / count;
  const periods: MilestonePeriod[] = [];

  for (let i = 0; i < count; i++) {
    periods.push({
      index: i,
      start: new Date(startDate.getTime() + periodMs * i),
      end: new Date(startDate.getTime() + periodMs * (i + 1)),
    });
  }

  return periods;
}

/**
 * Return the index of the current milestone based on today's date,
 * or -1 if before the first milestone, or count if after the last.
 */
export function getCurrentMilestoneIndex(
  startDate: Date,
  endDate: Date,
  count: number
): number {
  const now = new Date();
  if (now < startDate) return -1;
  if (now >= endDate) return count;
  const totalMs = endDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();
  return Math.min(Math.floor((elapsed / totalMs) * count), count - 1);
}

/**
 * Create an array of empty milestone achievements for a given count.
 */
export function initializeMilestones(count: number): MilestoneAchievement[] {
  return Array.from({ length: count }, (_, i) => ({
    milestone_index: i,
    achieved_value: null,
    recorded_at: null,
  }));
}

/**
 * Count how many milestones have a recorded achievement value.
 */
export function countRecordedMilestones(measures: Measure[]): {
  recorded: number;
  total: number;
} {
  let recorded = 0;
  let total = 0;
  for (const m of measures) {
    for (const ms of m.milestones) {
      total++;
      if (ms.achieved_value !== null) recorded++;
    }
  }
  return { recorded, total };
}

/**
 * Reset all milestone achievements to null (used when cloning a goal).
 */
export function resetMilestoneAchievements(measures: Measure[]): Measure[] {
  return measures.map((m) => ({
    ...m,
    milestones: m.milestones.map((ms) => ({
      ...ms,
      achieved_value: null,
      recorded_at: null,
    })),
  }));
}
